import { Injectable } from '@angular/core';
import {
  excelExportVertical,
  FmIncidentArchObj,
  VerticalExportConfig,
} from '@shared/utils/excel-export';
import {
  FmIncidentServiceLine,
  FmQuotationRequestLine,
  TaskViewModel,
} from '@core/api';

export interface FormOverrides {
  result?: string;
  validUntil?: Date | string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TaskExcelExportService {
  /**
   * Exportiert OPL Extern Task-Daten als Excel-Datei im vertikalen Format
   * @param data Die Task-Entity-Daten
   * @param locations Standort-Daten
   * @param fmIncidentArchObj Standort-Zusatzinfos (deviatingAddress, furtherLocationInformation)
   * @param formOverrides Optionale Formular-Überschreibungen (result)
   * @param fileName Dateiname für den Download
   * @param worksheetName Name des Worksheets
   */
  exportOplExtTask(
    data: TaskViewModel,
    locations: FmIncidentServiceLine[] | FmQuotationRequestLine[],
    fmIncidentArchObj: FmIncidentArchObj[] = [],
    formOverrides?: FormOverrides,
    fileName = 'Ticketdetails OPL Extern.xlsx',
    worksheetName = 'Ticketdetails OPL Extern',
    quotationLines?: any[],
    quotationLinesLabel?: string,
    hidePricesForLocations?: boolean,
    hidePurchasingInformation?: boolean,
    hideAuxilaryAccountAssignment?: boolean,
    showLocationsAfterPurchasingInfo?: boolean,
    serviceLineColumns?: any[],
    quotationLineColumns?: any[],
    appendQuotationLinesToLocationSections?: boolean,
    locationsLabel?: string,
    locationSumLabel?: string,
    totalSumLabel?: string,
    quotationLocationSumLabel?: string,
    quotationTotalSumLabel?: string
  ): void {
    // Erstelle eine Kopie der Daten mit Formular-Überschreibungen
    const exportData: any = { ...data };

    if (formOverrides?.result !== undefined) {
      exportData.result = formOverrides.result;
    }

    if (formOverrides?.validUntil !== undefined) {
      exportData.validUntil = formOverrides.validUntil;
    }

    // Basis-Felder (ohne Status-Felder am Ende)
    const baseFields = [
      { key: 'incident.ticketNumber', label: 'Ticketnummer' },
      { key: 'incident.requestType', label: 'Tickettyp' },
      { key: 'incident.name', label: 'Tickettitel' },
      { key: 'incident.description', label: 'Ticketbeschreibung' },
      { key: 'incident.incidentClass.incidentClass', label: 'Ticketklasse' },
      { key: 'incident.product.name', label: 'Produkt' },
      { key: 'incident.product.description', label: 'Produktbeschreibung' },
      {
        key: 'reporterFullName',
        label: 'Meldender Mitarbeiter',
        formatter: (data: any) => {
          const firstname =
            data?.incident?.fmServiceRequestExternal?.creator?.firstname || '';
          const lastname =
            data?.incident?.fmServiceRequestExternal?.creator?.lastname || '';
          const fullName = `${firstname} ${lastname}`.trim();
          return fullName || '-';
        },
      },
    ];

    const fieldsAfterReporter = [
      {
        key: 'incident.fmServiceRequestExternal.purchasingInformation',
        label: 'Information Einkauf',
      },
      {
        key: 'incident.fmServiceRequestExternal.frameworkContract.serviceProvider.name',
        label: 'Lieferant',
      },
      {
        key: 'incident.fmServiceRequestExternal.deliveryTargetDate',
        label: 'Zieltermin Leistungswunsch',
        formatter: (data: any) => {
          const date =
            data?.incident?.fmServiceRequestExternal?.deliveryTargetDate;
          if (!date) return '-';
          const d = new Date(date);
          return d.toLocaleDateString('de-DE');
        },
      },
      {
        key: 'incident.fmServiceRequestExternal.priority.defaultName',
        label: 'Priorität',
      },
      {
        key: 'incident.fmServiceRequestExternal.auxilaryAccountAssignment.value',
        label: 'Nebenkontierung',
      },
    ];

    const statusFields = [
      { key: 'subject', label: 'Aufgabe' },
      { key: 'result', label: 'Ergebnis' },
      {
        key: 'validUntil',
        label: 'Angebotsbindefrist',
        formatter: (data: any) => {
          const value = data?.validUntil;
          if (!value) return '-';
          return new Date(value).toLocaleDateString('de-DE');
        },
      },
      { key: 'status', label: 'Aktueller Status' },
    ];

    let filteredFields = [
      ...baseFields,
      ...fieldsAfterReporter,
      ...statusFields,
    ];

    if (hidePurchasingInformation) {
      filteredFields = filteredFields.filter(
        field =>
          field.key !==
          'incident.fmServiceRequestExternal.purchasingInformation'
      );
    }

    if (hideAuxilaryAccountAssignment) {
      filteredFields = filteredFields.filter(
        field =>
          field.key !==
          'incident.fmServiceRequestExternal.auxilaryAccountAssignment.value'
      );
    }

    const config: VerticalExportConfig = {
      fields: filteredFields,
      locationsLabel: locationsLabel || 'Standorte',
      showLocationsAfterPurchasingInfo,
    };

    excelExportVertical(
      exportData,
      config,
      locations,
      worksheetName,
      fileName,
      fmIncidentArchObj,
      quotationLines,
      quotationLinesLabel,
      hidePricesForLocations,
      serviceLineColumns,
      quotationLineColumns,
      locationSumLabel,
      totalSumLabel,
      quotationLocationSumLabel,
      quotationTotalSumLabel
    );
  }
}
